import React from 'react';
import PropTypes from 'prop-types';
import cx from 'classnames';
import { Box, Typography } from '@material-ui/core';
import { mpsFormatter } from '../../modules/performance/utils';

const MIN_MPS_VALUE_POSITION = 12;
const MAX_MPS_VALUE_POSITION = 88;

/**
 * Render Scorecard Progress bar according to wireframes https://xd.adobe.com/view/e097af00-1c5f-49d3-8d40-4f8a0c46a69c-b5c4/screen/ebcddaa4-706e-4f7f-981a-de136fd77d09/specs/
 * Specially for https://neimanmarcus.atlassian.net/browse/OSP-6253
 */
const ScorecardProgressBar = ({
  classes,
  netSales,
  goal,
  mps,
  isExpandedScorecard,
  scorecardCategory,
  displayMPS,
  expScorecardRedesignEnabled,
  userBrand,
}) => {
  const isBGUser = userBrand === 'BG';
  const mpsDisplay = expScorecardRedesignEnabled && isBGUser ? 'Min' : 'MPS';

  if (goal <= 0 && mps <= 0) {
    return null;
  }

  let goalProgress = 0;
  let mpsProgress = 0;

  if (goal > 0) {
    goalProgress = netSales / goal > 1 ? 100 : Math.round((netSales / goal) * 100);
    mpsProgress = mps / goal > 1 ? 100 : Math.round((mps / goal) * 100);
  } else if (mps > 0) {
    goalProgress = netSales / mps > 1 ? 100 : Math.round((netSales / mps) * 100);
    mpsProgress = 100;
  }

  let mpsValuePosition = mpsProgress;

  if (mpsValuePosition < MIN_MPS_VALUE_POSITION) {
    mpsValuePosition = MIN_MPS_VALUE_POSITION;
  } else if (mpsValuePosition > MAX_MPS_VALUE_POSITION) {
    mpsValuePosition = MAX_MPS_VALUE_POSITION;
  }

  return (
    <Box className={classes.container}>
      <Box className={!isExpandedScorecard ? classes.barContainer : classes.barContainerExp}>
        <div
          data-testid="scorecardprogressbar-fill"
          className={cx(classes.bar, goalProgress < mpsProgress && classes.redBar)}
          style={{ width: `${goalProgress}%` }}
        />

        {mpsProgress > 0 && displayMPS && (
          <div
            className={!isExpandedScorecard ? classes.mpsIndicator : classes.mpsIndicatorExp}
            style={{ width: `${mpsProgress}%` }}
          />
        )}
      </Box>
      {displayMPS && (
        <Box
          data-testid="scorecardprogressbar-mps"
          className={!isExpandedScorecard ? classes.mpsContainer : classes.mpsContainerExp}
          style={{ paddingLeft: `${mpsValuePosition}%` }}
        >
          <Typography variant="body1" color="textSecondary" className={classes.mpsValue}>
            {!isExpandedScorecard && mps > 0 && `${mpsDisplay}: ${mpsFormatter(mps, scorecardCategory)}`}
          </Typography>
        </Box>
      )}
    </Box>
  );
};

ScorecardProgressBar.propTypes = {
  classes: PropTypes.objectOf(PropTypes.string),
  netSales: PropTypes.number,
  goal: PropTypes.number,
  mps: PropTypes.number,
  isExpandedScorecard: PropTypes.bool,
  scorecardCategory: PropTypes.oneOf(['netSales', 'wardrobing', 'clientConnect', 'averageTrip']),
  displayMPS: PropTypes.bool,
  userBrand: PropTypes.string,
};

ScorecardProgressBar.defaultProps = {
  classes: {},
  displayMPS: true,
  userBrand: '',
};

export default ScorecardProgressBar;
